import {
  CustomLabel,
  DocumentCategoryWithoutReadIdAndClientId,
  FileCategory,
  FolderEntity,
  Language,
  canDelete,
  getTypedLanguage,
  isNilOrEmpty,
} from '@rentguru/commons-utils';
import { toast } from '@up2rent/ui-new';
import { isEmpty, isNil, last } from 'lodash';
import { IntlFormatters } from 'react-intl';

import {
  ConfirmationDialogContext,
  ConfirmationDialogOptions,
  DialogConfig,
} from 'src/hooks/ConfirmationDialogContext';
import { FolderIndexes, type FolderDatabaseModel } from 'src/hooks/Folderhook/folderContextUtils';
import { FolderDatabaseContext } from 'src/hooks/Folderhook/FolderDatabaseContext';
import { FolderWithDBContext } from 'src/hooks/Folderhook/FolderWithDBContext';
import { SignatureDocumentsContext } from 'src/hooks/SignatureDocumentContext';
import { translateText } from 'src/utils/translationUtils';

const START_DEPTH = '/';

export const checkAddLeaseContract = async (
  filesInLeaseFolder: FolderDatabaseModel[],
  files: File[],
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation'],
  getSignatureDocumentForFile: SignatureDocumentsContext['getSignatureDocumentForFile']
): Promise<{ continueDrop: boolean; fileToReplace?: FolderDatabaseModel }> => {
  if (files.length > 1) {
    toast.error(formatMessage({ id: 'folders.dragAndDropWrapper.leaseContractUploadDialogError' }));
    return { continueDrop: false };
  }
  if (!isEmpty(filesInLeaseFolder)) {
    const hasSignature = await getSignatureDocumentForFile(filesInLeaseFolder[0].documentId!);
    if (hasSignature) {
      toast.error(formatMessage({ id: 'folders.fileActions.replaceLeaseWithSignature' }));
      return { continueDrop: false };
    }

    const confirmation = await getConfirmation({
      title: formatMessage({ id: 'folders.dragAndDropWrapper.leaseContractUploadDialogTitle' }),
      text: formatMessage({ id: 'folders.dragAndDropWrapper.leaseContractUploadDialogText' }),
      actionButtonLabel: formatMessage({ id: 'lease.detail.action.confirm' }),
      formatMessage,
    });
    if (confirmation === ConfirmationDialogOptions.ACTION_BUTTON) {
      return { continueDrop: true, fileToReplace: filesInLeaseFolder[0] };
    }
    return { continueDrop: false };
  }
  return { continueDrop: true };
};

export const getTranslatedCategory = (labels: CustomLabel[], language: Language): string => {
  const translatedLabel = labels.find((label) => getTypedLanguage(label.language) === language);
  return translatedLabel ? translatedLabel.label : '';
};

export const getTranslatedLabel = (folder: FolderDatabaseModel, language: Language): string =>
  folder.labels ? getTranslatedCategory(folder.labels, language) : folder.translatedLabel!;

export const getFolderLabel = (folder: FolderDatabaseModel): string | CustomLabel[] =>
  folder.labels ? folder.labels : folder.translatedLabel!;

export const getFolderDepthString = (folder: FolderDatabaseModel | undefined, language: Language): string => {
  if (isNil(folder) || isEmpty(folder.depthString)) {
    return '/';
  }
  const depthString = folder.depthString
    .map((depth) => {
      if (typeof depth === 'string' || !depth) {
        return depth;
      }
      return getTranslatedCategory(depth, language);
    })
    .join('/');

  return '/' + depthString;
};

export const getRelativeFolderDepthString = (
  folder: FolderDatabaseModel | undefined,
  entityRootFolder: FolderDatabaseModel,
  language: Language
): string => {
  if (isNil(folder) || isNilOrEmpty(folder.depthString)) {
    return START_DEPTH;
  }
  const levelsToSkip = entityRootFolder.depthString.length + 1;
  const relativeParts =
    folder?.depthString.slice(levelsToSkip).map((depth) => {
      if (typeof depth === 'string' || !depth) {
        return depth;
      }
      return getTranslatedCategory(depth, language);
    }) ?? [];
  if (isEmpty(relativeParts)) {
    return START_DEPTH;
  }
  return START_DEPTH + relativeParts.join('/');
};

const askForDeleteConfirmation = async (
  text: string,
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation']
) =>
  await getConfirmation({
    title: formatMessage({ id: 'folders.fileActions.deleteFolderTitle' }),
    text,
    actionButtonLabel: formatMessage({ id: 'folders.fileActions.deleteAll' }),
    thirdButtonLabel: formatMessage({ id: 'folders.fileActions.deleteOnlyFiles' }),
    formatMessage,
  });

const deleteWithAdmin = async (
  fileFolders: FolderDatabaseModel[],
  entityFolders: FolderDatabaseModel[],
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation']
): Promise<FolderDatabaseModel[]> => {
  const result = await askForDeleteConfirmation(
    formatMessage({ id: 'folders.fileActions.deleteEveryFile' }),
    formatMessage,
    getConfirmation
  );
  if (isNil(result)) {
    return [];
  }
  if (result === ConfirmationDialogOptions.THIRD_BUTTON) {
    // User chose to delete only files, not the folder
    return fileFolders;
  }
  if (result === ConfirmationDialogOptions.ACTION_BUTTON) {
    // User chose to delete the folder and all files
    return entityFolders;
  }
  return [];
};

const deleteWithConfirmation = async (
  folder: FolderDatabaseModel,
  fileFolders: FolderDatabaseModel[],
  language: Language,
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation']
): Promise<{ foldersToDelete: FolderDatabaseModel[]; shouldContinue: boolean }> => {
  const result = await askForDeleteConfirmation(
    formatMessage({ id: 'folders.fileActions.deleteFolderMessage' }, { name: getTranslatedLabel(folder, language) }),
    formatMessage,
    getConfirmation
  );

  if (result === ConfirmationDialogOptions.THIRD_BUTTON) {
    return { foldersToDelete: fileFolders, shouldContinue: true };
  }

  return { foldersToDelete: [], shouldContinue: isNil(result) };
};

export const deleteFolderLogic = async (
  folders: FolderDatabaseModel[],
  deleteFolders: FolderWithDBContext['deleteFolders'],
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter'],
  settingsManagementWrite: boolean,
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation'],
  language: Language
): Promise<void> => {
  const foldersToDelete: FolderDatabaseModel[] = [];
  for (const folder of folders) {
    if (folder.folderEntity === FolderEntity.FILE) {
      foldersToDelete.push(folder);
      continue;
    }

    const entityFolders = await getFoldersWithCustomFilter(
      (folderForFilter) =>
        folderForFilter.documentCategoryId === folder.documentCategoryId ||
        folderForFilter.depth.includes(folder.documentCategoryId)
    );

    const currentDepthFolders = entityFolders.filter((folderForFilter) => folderForFilter.entityId === folder.entityId);
    const fileFolders = entityFolders.filter(
      (fileFolder) => fileFolder.entityId === folder.entityId && fileFolder.folderEntity === FolderEntity.FILE
    );

    if (
      entityFolders.some(
        (entityFolder) => entityFolder.entityId !== folder.entityId && entityFolder.folderEntity === FolderEntity.FILE
      )
    ) {
      if (settingsManagementWrite) {
        //When the user has permission to delete folders on other entities
        // we ask for confirmation to delete all files in the folder
        const newFoldersToDelete = await deleteWithAdmin(fileFolders, entityFolders, formatMessage, getConfirmation);
        foldersToDelete.push(...newFoldersToDelete);
      }
      continue;
    }

    if (currentDepthFolders.some((currentDepthFolder) => currentDepthFolder.folderEntity === FolderEntity.FOLDER)) {
      const confirmation = await deleteWithConfirmation(folder, fileFolders, language, formatMessage, getConfirmation);
      if (confirmation.shouldContinue) {
        foldersToDelete.push(...confirmation.foldersToDelete);
        continue;
      }
    }
    foldersToDelete.push(...(!isEmpty(entityFolders) ? entityFolders : [folder]));
  }
  await deleteFolders(foldersToDelete);
};

export const getDocumentCategoryFolder = async (
  EntityId: string,
  documentCategoryId: string,
  getFoldersWithCustomFilter: FolderDatabaseContext['getFoldersWithCustomFilter']
): Promise<FolderDatabaseModel | undefined> =>
  await getFoldersWithCustomFilter(
    (folder) =>
      folder.entityId === EntityId &&
      folder.documentCategoryId === documentCategoryId &&
      folder.folderEntity === FolderEntity.FOLDER
  ).then((folders) => folders[0]);

export const handleSaveWithTranslation = async (labels: CustomLabel[]) => {
  let translate = false;
  const filledLanguages: Language[] = labels.reduce((acc, label) => {
    if (!isEmpty(label.label)) {
      acc.push(label.language as Language);
      return acc;
    }
    translate = true;
    return acc;
  }, [] as Language[]);

  if (isEmpty(filledLanguages)) {
    return;
  }
  if (translate && !isEmpty(filledLanguages)) {
    const allLanguages = Object.values(Language);
    const textToTranslate = labels.find((label) => label.language === filledLanguages[0])?.label;
    const destinationLanguages = allLanguages.filter((lang) => !filledLanguages.includes(lang));
    for (const destinationLanguage of destinationLanguages) {
      const newText = await translateText(textToTranslate!, filledLanguages[0], destinationLanguage);
      const labelToUpdate = labels.find((label) => label.language.toUpperCase() === destinationLanguage);
      if (labelToUpdate) {
        labelToUpdate.label = newText;
      }
    }
  }

  return labels;
};

export const checksForAddingFile = async (
  selectedCategory: DocumentCategoryWithoutReadIdAndClientId,
  folderNavigation: (FolderDatabaseModel | undefined)[],
  files: File[],
  getFoldersByIndex: FolderDatabaseContext['getFoldersByIndex'],
  deleteFolders: FolderWithDBContext['deleteFolders'],
  formatMessage: IntlFormatters['formatMessage'],
  getConfirmation: ConfirmationDialogContext['getConfirmation'],
  getSignatureDocumentForFile: SignatureDocumentsContext['getSignatureDocumentForFile']
) => {
  const leaseFileToReplace: FolderDatabaseModel[] = [];
  if (selectedCategory.fileCategory === FileCategory.LEASE_CONTRACT) {
    const currentEntityFolders = await getFoldersByIndex(
      FolderIndexes.ENTITY_ID,
      last(folderNavigation)?.entityId || ''
    );

    const targetFolder = currentEntityFolders.find(
      (entityFolder) =>
        entityFolder.folderEntity === FolderEntity.FOLDER && entityFolder.documentCategoryId === selectedCategory?.id
    );

    const leaseFiles = await getFoldersByIndex(
      FolderIndexes.DEPTH,
      `${targetFolder?.depth}.${targetFolder?.documentCategoryId}`
    );

    const { continueDrop, fileToReplace } = await checkAddLeaseContract(
      leaseFiles,
      files,
      formatMessage,
      getConfirmation,
      getSignatureDocumentForFile
    );
    if (!continueDrop) {
      return false;
    }

    if (continueDrop && fileToReplace) {
      leaseFileToReplace.push(fileToReplace);
    }
  }
  if (!isEmpty(leaseFileToReplace)) {
    await deleteFolders(leaseFileToReplace);
  }
  if (selectedCategory && !canDelete(selectedCategory?.fileCategory)) {
    const result = await getConfirmation({
      title: formatMessage({ id: 'warning' }),
      text: formatMessage({ id: 'folders.fileActions.cantDeleteFileText' }),
      actionButtonLabel: formatMessage({ id: 'lease.detail.action.confirm' }),
      formatMessage,
    } as DialogConfig);

    if (result !== ConfirmationDialogOptions.ACTION_BUTTON) {
      return false;
    }
  }
  return true;
};
